'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Button, Stack, Typography } from '@mui/material';
import type { AuthV2AssociateSoftwareTokenResponse, AuthV2VerifySoftwareTokenResponse } from '@repo/api-gw-sdk';
import { useEffect, useRef, useState } from 'react';
import { getApiErrorMessage } from '@/utils/apiError';
import { LoginCard } from '../loginCard';
import { VerificationCodeField } from '../verificationCodeField';
export const MfaSetupStep = t0 => {
  const $ = _c(22);
  const {
    completing,
    completeError,
    onAssociate,
    onVerify,
    onComplete
  } = t0;
  const [setup, setSetup] = useState(null);
  const [associateError, setAssociateError] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [verifiedSession, setVerifiedSession] = useState(null);
  const associated = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onAssociate) {
    t1 = () => {
      if (associated.current) {
        return;
      }
      associated.current = true;
      onAssociate().then(setSetup).catch(e => setAssociateError(getApiErrorMessage(e, "Could not start MFA setup")));
    };
    t2 = [onAssociate];
    $[0] = onAssociate;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== code || $[4] !== completing || $[5] !== onComplete || $[6] !== onVerify || $[7] !== setup || $[8] !== verifiedSession || $[9] !== verifying) {
    t3 = () => {
      if (!setup?.session || !code || verifying || completing) {
        return;
      }
      if (verifiedSession) {
        onComplete(verifiedSession);
        return;
      }
      setVerifyError(null);
      setVerifying(true);
      onVerify(setup.session, code).then(res => {
        if (res.status === "SUCCESS" && res.session) {
          setVerifiedSession(res.session);
          onComplete(res.session);
        } else {
          setVerifyError("The verification code is incorrect. Please try again.");
        }
      }).catch(e_0 => setVerifyError(getApiErrorMessage(e_0, "Could not verify code"))).finally(() => setVerifying(false));
    };
    $[3] = code;
    $[4] = completing;
    $[5] = onComplete;
    $[6] = onVerify;
    $[7] = setup;
    $[8] = verifiedSession;
    $[9] = verifying;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const submit = t3;
  const error = associateError ?? verifyError ?? completeError;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Typography variant="subtitle1" className="text-center">Set up two-factor authentication. Scan this QR code with your authenticator app, then enter the generated code:</Typography>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== associateError || $[13] !== code || $[14] !== completing || $[15] !== setup || $[16] !== submit || $[17] !== verifying) {
    t5 = setup ? <Stack sx={{
      width: "100%",
      alignItems: "center",
      gap: "24px"
    }}><Box component="img" src={setup.qrCodeImage} alt="Authenticator setup QR code" data-testid="login-v2-mfa-setup-qr" sx={{
        width: "200px",
        height: "200px"
      }} /><Stack sx={{
        width: "100%",
        alignItems: "center",
        gap: "4px"
      }}><Typography variant="subtitle2" className="text-center">Or enter this key manually:</Typography><Typography variant="subtitle1Strong" className="text-center" data-testid="login-v2-mfa-setup-secret" sx={{
          wordBreak: "break-all"
        }}>{setup.secretCode}</Typography></Stack><Stack component="form" onSubmit={event => {
        event.preventDefault();
        submit();
      }} sx={{
        width: "100%",
        gap: "40px"
      }}><VerificationCodeField id="login-v2-mfa-setup-code" value={code} onChange={value => {
          setCode(value);
          setVerifyError(null);
        }} /><Button fullWidth={true} type="submit" variant="contained" disabled={!code || verifying || completing} data-testid="login-v2-mfa-setup-submit" sx={{
          height: "48px"
        }}>{verifying || completing ? "Verifying\u2026" : "Verify"}</Button></Stack></Stack> : !associateError && <Typography variant="body1" className="text-center" data-testid="login-v2-mfa-setup-loading">Preparing setup…</Typography>;
    $[12] = associateError;
    $[13] = code;
    $[14] = completing;
    $[15] = setup;
    $[16] = submit;
    $[17] = verifying;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== error || $[20] !== t5) {
    t6 = <LoginCard error={error}>{t4}{t5}</LoginCard>;
    $[19] = error;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};